import { useState, useEffect, type ComponentType, type SVGProps } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import UserAvatar from '../components/UserAvatar';
import { ADMIN_USER_IDS, ADMIN_EMAILS } from '../lib/admin';
import { decodeJWT } from '../utils/auth';

import {
  ArrowRightStartOnRectangleIcon,
  BriefcaseIcon,
  ChatBubbleLeftEllipsisIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  CreditCardIcon,
  DocumentIcon,
  EnvelopeIcon,
  KeyIcon,
  MoonIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  SunIcon,
  SwatchIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

interface SettingItem {
  icon: IconComponent;
  title: string;
  onClick?: () => void;
  variant?: 'default' | 'danger';
}

type ThemeMode = 'light' | 'dark' | 'system';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<ThemeMode>('light');
  const [showThemeOptions, setShowThemeOptions] = useState(false);

  // Synchronous admin check — no async needed, just string comparison
  const token = localStorage.getItem('planbase_token');
  const userData = localStorage.getItem('planbase_user');
  let isAdmin = false;
  if (token) {
    try {
      const payload = decodeJWT(token);
      const userId = payload?.sub || payload?.user_id || payload?.id;
      let email = payload?.email;
      if (!email && userData) { try { email = JSON.parse(userData).email; } catch {} }
      isAdmin = ADMIN_USER_IDS.includes(userId) || (email && ADMIN_EMAILS.includes(email.toLowerCase()));
    } catch {}
  }

  useEffect(() => {
    const saved = localStorage.getItem('planbase_theme') as ThemeMode;
    if (saved) { setTheme(saved); applyTheme(saved); }
  }, []);

  const applyTheme = (mode: ThemeMode) => {
    if (mode === 'dark') document.documentElement.classList.add('dark');
    else if (mode === 'light') document.documentElement.classList.remove('dark');
    else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
    }
  };

  const handleThemeChange = (mode: ThemeMode) => {
    setTheme(mode); localStorage.setItem('planbase_theme', mode); applyTheme(mode); setShowThemeOptions(false);
  };

  const groups = [
    {
      items: [
        { icon: PencilSquareIcon, title: 'Edit Profile', onClick: () => navigate('/profile/edit') },
        { icon: BriefcaseIcon, title: 'Portfolio Setup', onClick: () => navigate('/portfolio-setup') },
        { icon: CreditCardIcon, title: 'Billing', onClick: () => navigate('/pricing') },
        { icon: KeyIcon, title: 'Security', onClick: () => navigate('/profile/security') },
        { icon: SwatchIcon, title: 'Theme', onClick: () => setShowThemeOptions(!showThemeOptions) },
        ...(isAdmin ? [{ icon: ShieldCheckIcon, title: 'Admin', onClick: () => navigate('/admin') }] : []),
      ] as SettingItem[],
    },
    {
      items: [
        { icon: SparklesIcon, title: 'How to use Orbi', onClick: () => navigate('/help') },
        { icon: UserGroupIcon, title: 'Community', onClick: () => window.open('https://www.reddit.com/r/Planbase/', '_blank') },
        { icon: ChatBubbleLeftEllipsisIcon, title: 'Share Feedback', onClick: () => window.open('https://collect.usegately.com/t/form_w6ccu8my', '_blank') },
        { icon: QuestionMarkCircleIcon, title: 'Help & Support', onClick: () => window.open('https://desqly.usegately.com/h/S2D2TI', '_blank') },
      ] as SettingItem[],
    },
    {
      items: [
        { icon: DocumentIcon, title: 'Terms & Conditions', onClick: () => window.open('https://www.notion.so/oluwatobi-olarewaju/Terms-of-service-2348673c0e40807886bfd541bfa262bf', '_blank') },
        { icon: ShieldCheckIcon, title: 'Privacy Policy', onClick: () => window.open('https://planbase.app/privacy', '_blank') },
        { icon: TrashIcon, title: 'Delete Account', onClick: () => window.open('https://app.usegately.com/f/W4MLQ1', '_blank'), variant: 'danger' as const },
      ] as SettingItem[],
    },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Profile</h1>
        <Button onClick={logout} variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 hover:border-red-300 dark:border-red-900 dark:hover:bg-red-950">
          <ArrowRightStartOnRectangleIcon className="mr-1.5 h-3.5 w-3.5" />Log out
        </Button>
      </div>

      {/* User Card */}
      <div className="bg-card border border-border rounded-2xl p-5 flex items-center gap-4">
        <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} size="lg" className="h-14 w-14 text-xl flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5 truncate">
            <EnvelopeIcon className="h-3 w-3 flex-shrink-0" />{user?.email}
          </p>
        </div>
      </div>

      {/* Setting Groups */}
      {groups.map((group, gi) => (
        <div key={gi} className="bg-card border border-border rounded-2xl overflow-hidden">
          {group.items.map((item, ii) => {
            const isDanger = item.variant === 'danger';
            const isTheme = item.title === 'Theme';
            const ItemIcon = item.icon;
            return (
              <div key={ii}>
                {ii > 0 && <div className="border-t border-border mx-4" />}
                <button
                  onClick={item.onClick}
                  className={`w-full flex items-center justify-between px-4 py-3.5 transition-colors ${
                    isDanger ? 'hover:bg-destructive/5' : 'hover:bg-muted/60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <ItemIcon className={`h-4 w-4 flex-shrink-0 ${isDanger ? 'text-destructive' : 'text-muted-foreground'}`} />
                    <span className={`text-sm ${isDanger ? 'text-destructive' : 'text-foreground'}`}>{item.title}</span>
                  </div>
                  <ChevronRightIcon className={`h-4 w-4 ${isDanger ? 'text-destructive/50' : 'text-muted-foreground/50'} ${isTheme && showThemeOptions ? 'rotate-90' : ''} transition-transform`} />
                </button>
                {isTheme && showThemeOptions && (
                  <div className="mx-4 mb-3 p-1 bg-muted rounded-xl flex gap-1">
                    {([['light', 'Light', SunIcon], ['dark', 'Dark', MoonIcon], ['system', 'System', ComputerDesktopIcon]] as const).map(([val, label, Ic]) => (
                      <button
                        key={val}
                        onClick={() => handleThemeChange(val)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                          theme === val ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Ic className="h-3.5 w-3.5" />{label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
